import type { RouteLocationNormalizedLoaded } from 'vue-router';

import type { ChatPage, ChatPageEntities } from '@/composables/api/bekki/types';
import { getRouteTitle } from '@/composables/usePageTitle';
import { i18n } from '@/i18n';
import dayjs from '@/plugins/dayjs';

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ID_PATTERN = /^[\w-]{1,64}$/;

export const isUuid = (value: string): boolean => UUID_PATTERN.test(value);
export const isId = (value: string): boolean => ID_PATTERN.test(value);
export const isDate = (value: string): boolean =>
  dayjs(value, 'YYYY-MM-DD', true).isValid();

interface EntityRule {
  key: keyof ChatPageEntities;
  isValid: (value: string) => boolean;
}

const ENTITY_RULES: Record<string, EntityRule> = {
  tourId: { key: 'tourId', isValid: isUuid },
  tour: { key: 'tourNumber', isValid: isId },
  date: { key: 'date', isValid: isDate },
  parcelId: { key: 'parcelId', isValid: isId },
  jobId: { key: 'jobId', isValid: isId },
  internalDepotId: { key: 'internalDepotId', isValid: isId }
};

const ROUTES_WITHOUT_ENTITIES = new Set(['tourCommanderTourViewContainer']);

const getParam = (
  params: RouteLocationNormalizedLoaded['params'],
  name: string
): string | undefined => {
  const value = params[name];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const getEntities = (
  params: RouteLocationNormalizedLoaded['params']
): ChatPageEntities => {
  const entities: ChatPageEntities = {};

  for (const [name, { key, isValid }] of Object.entries(ENTITY_RULES)) {
    const value = getParam(params, name);
    if (value && isValid(value)) entities[key] = value;
  }

  const dates = getParam(params, 'dates')?.split('_') ?? [];
  const [dateFrom, dateTo] = dates;
  if (
    dates.length === 2 &&
    dateFrom &&
    dateTo &&
    isDate(dateFrom) &&
    isDate(dateTo)
  ) {
    entities.dateFrom = dateFrom;
    entities.dateTo = dateTo;
  }

  return entities;
};

export const getPageContext = (
  route: RouteLocationNormalizedLoaded
): ChatPage | undefined => {
  const { name } = route;
  if (typeof name !== 'string') return undefined;

  const page: ChatPage = { route: name };

  const title = getRouteTitle(route, (key) => i18n.global.t(key));
  if (title) page.title = title;

  if (!ROUTES_WITHOUT_ENTITIES.has(name)) {
    const entities = getEntities(route.params);
    if (Object.keys(entities).length > 0) page.entities = entities;
  }

  return page;
};
